import type { BlogPost } from "./types";

// Image guidance from source doc (photo omitted for now; add to public/blog/ later):
//  *   AI robot analysing a website interface for accessibility compliance — alt: "Stylised illustration of an artificial intelligence system scanning a website interface with visual indicators highlighting accessible and inaccessible elements"
//  *   Split view: accessibility overlay widget versus clean semantic HTML code — alt: "Split-screen comparison showing an accessibility overlay widget popup on a website on the left, and clean semantic HTML code with proper ARIA labels and heading structure on the right"
const post: BlogPost = {
  slug: "ai-accessibility-tools-overlays-what-works",
  title: "AI and Accessibility: What Helps and Why Overlays Don't",
  metaTitle: "AI and Accessibility: Why Overlays Fail",
  subtitle:
    "Where AI genuinely helps with accessibility, why overlays do not make a site compliant, and how to judge the tools you are offered.",
  date: "2026-07-15",
  photo: "/blog/ai-accessibility-tools-overlays-what-works.svg",
  alt:
    "Stylised illustration of an artificial intelligence system scanning a website interface with visual indicators highlighting accessible and inaccessible elements",
  author: "AccessBharat",
  tag: "AI",
  excerpt:
    "Where AI genuinely helps with accessibility, why overlays do not make a site compliant, and how to judge the tools you are offered.",
  body: `AI has made some parts of accessibility easier and created new problems in others. Automatic captions, AI-generated alt text and voice interfaces give more people access to content than before. The same wave has also produced products that promise to fix accessibility automatically, and those keep falling short.

## Where AI helps

Captioning is the clearest win. Google's Live Caption, YouTube's automatic subtitles and Microsoft Azure AI Captioning make video usable for deaf and hard-of-hearing people at a scale that human transcription could not match on cost.

AI-generated alt text, in tools such as Microsoft Seeing AI and Google Lookout, helps content teams that would otherwise publish images with no description at all. It is better than nothing, but treat it as a starting point. Complex, contextual or brand-specific images still need a person to write or review the description.

## The overlay problem

Overlays are JavaScript widgets that claim to make any website WCAG-compliant with a single line of code. They have been marketed hard for about five years, and the accessibility community's view is consistent: they do not make a site accessible.

Screen reader users, the people overlays say they serve, report that the widgets often clash with their own assistive technology and make sites harder to use. Several overlay vendors have been sued by users who said the overlay left the site less accessible than before.

AI can assist accessibility work. It cannot replace it.

## How to evaluate an AI accessibility tool

Automated tools such as axe, WAVE and Lighthouse catch roughly 30-40% of WCAG failures. AI-based tools are improving, but the gap is still large. Before you buy one, ask:

- What share of WCAG success criteria does it actually test?
- Can it produce a report that references specific WCAG criteria?
- Has it been tested with people who use assistive technology?
- If you remove the tool, is the underlying site still accessible?

## A sensible split

Use AI for volume, speed and first-pass detection. Use people for judgment: checking fixes, testing with assistive technology, and writing documentation that will hold up if it is challenged. Neither is enough on its own.
`,
};

export default post;
